import { motion } from 'framer-motion';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { ZoomOut, ZoomIn, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils/utils';
import { formatRef } from '@/lib/utils/bible-utils';
import { useMemo } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ContextBreadcrumbsProps {
    bookName: string;
    breadcrumbPath: OutlineNode[];
    canZoomOut: boolean;
    onNavigateToNode: (nodeId: string) => void;
    onNavigateToParent: () => void;
    onNavigateBackIn: () => void;
    onClose: () => void;
    bookId: string;
}

export function ContextBreadcrumbs({
    bookName,
    breadcrumbPath,
    canZoomOut,
    onNavigateToNode,
    onNavigateToParent,
    onNavigateBackIn,
    onClose,
    bookId
}: ContextBreadcrumbsProps) {
    const filteredPath = useMemo(() => breadcrumbPath.filter(n => n.children?.length > 0), [breadcrumbPath]);
    return (
        <motion.div 
            className="backdrop-blur-md bg-white/10 dark:bg-gray-900/20 border border-white/20 dark:border-gray-600/30 rounded-lg px-3 py-2 shrink-0"
            layout
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        >
            <div className="flex items-center justify-between gap-2">
                {/* Breadcrumb path */}
                <div className="flex items-center gap-2 text-white text-sm flex-1 min-w-0 overflow-hidden">
                    <Button 
                        variant="ghost"
                        size="sm"
                        className="font-semibold hover:bg-white/10 text-white shrink-0" 
                        onClick={() => onNavigateToNode(bookId)}
                    >
                        {bookName}
                    </Button>
                    
                    {filteredPath.length > 0 && (
                        <>
                            {/* Desktop: Show all breadcrumbs */}
                            {filteredPath.map((n) => (
                                <div key={n.id} className="hidden md:flex items-center gap-2 shrink-0">
                                    <span className="text-white/60">/</span>
                                    <Button 
                                        variant="ghost"
                                        size="sm"
                                        className="hover:bg-white/10 text-white truncate" 
                                        onClick={() => onNavigateToNode(n.id)}
                                    >
                                        {n.title}
                                    </Button>
                                </div>
                            ))}
                            
                            {/* Mobile: Show dropdown with all levels */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="flex md:hidden hover:bg-white/10 text-white h-8 w-8"
                                    >
                                        <ChevronDown className="w-4 h-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="bg-gray-900/95 border-white/20">
                                    {filteredPath.map((n) => (
                                        <DropdownMenuItem
                                            key={n.id}
                                            onClick={() => onNavigateToNode(n.id)}
                                            className="text-white hover:bg-white/10 cursor-pointer"
                                        >
                                            <div className="flex flex-col">
                                                <span className="font-semibold">{formatRef(n.ref)}</span>
                                                <span className="text-xs opacity-80">{n.title}</span>
                                            </div>
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    )}
                </div>
                
                {/* Zoom controls */}
                <div className="flex gap-1 shrink-0">
                    <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onNavigateToParent}
                                disabled={!canZoomOut}
                                className={cn(
                                    "hover:bg-white/10 text-white h-8 w-8",
                                    !canZoomOut && "opacity-30 cursor-not-allowed"
                                )}
                            >
                                <ZoomOut className="w-4 h-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Zoom out to parent</p>
                        </TooltipContent>
                    </Tooltip>
                    
                    <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onNavigateBackIn}
                                className="hover:bg-white/10 text-white h-8 w-8"
                            >
                                <ZoomIn className="w-4 h-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Zoom back in</p>
                        </TooltipContent>
                    </Tooltip>
                    
                    {/* Separator */}
                    <div className="w-px h-4 bg-white/20" />
                    
                    <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onClose}
                                className="flex items-center gap-1.5 hover:bg-white/10 text-white"
                            >
                                <X className="w-4 h-4" />
                                <span className="hidden md:inline text-sm">Close</span>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Return to passage</p>
                        </TooltipContent>
                    </Tooltip>
                </div>
            </div>
        </motion.div>
    );
}
